import PQueue from 'p-queue'
import {EventEmitter} from 'eventemitter3'
import type {
  BalanceInfo,
  DemoProject,
  ResponseFormat,
  StorageOptions,
  SubscribeDevClientConfig,
  SubscriptionStatus,
  UsageLimits,
  UserStorage,
} from './types'
import {
  AccessDeniedError,
  AuthenticationError,
  InsufficientBalanceError,
  NotFoundError,
  RateLimitError,
  SubscribeDevError,
  UnsubscribedError,
  ValidationError
} from './errors'
import { z } from 'zod';

/**
 * SubscribeDevClient - Drop-in replacement for Replicate.AI and OpenRouter with end-user-billing and rate limiting
 */
export class SubscribeDevClient extends EventEmitter {
  private readonly config: SubscribeDevClientConfig & {
    baseUrl: string
    timeout: number
    maxRetries: number
    debug: boolean
  }
  private readonly queue: PQueue
  private readonly fetch: typeof fetch

  constructor(config: SubscribeDevClientConfig) {
    super()
    
    // Set defaults
    this.config = {
      baseUrl: config.baseUrl || 'https://subscribedev.volterapp-dev.com',
      timeout: config.timeout || 300000, // 5 minutes
      maxRetries: config.maxRetries || 2,
      debug: config.debug || false,
      ...config
    }

    // Create request queue to handle rate limiting
    this.queue = new PQueue({
      concurrency: 10, // Max concurrent requests
      interval: 60000, // 1 minute
      intervalCap: 60 // Max requests per minute
    })

    // Use global fetch or polyfill - bind to preserve 'this' context for browsers
    this.fetch = globalThis.fetch?.bind(globalThis)
    if (!this.fetch) {
      throw new Error('fetch is not available. Please provide a fetch polyfill.')
    }

    this.debug('SubscribeDevClient initialized', { baseUrl: this.config.baseUrl })
  }

  get pricingUrl() {
    return `${this.config.baseUrl}/subscribe`
  }

  /**
   * Run a prediction
   * Note: Now returns completed predictions directly - no polling needed
   * 
   * @param model - The model to use for the prediction
   * @param options - Configuration options for the prediction
   * @param options.input - Input data (can contain prompt OR messages array with multimodal content)
   * @param options.response_format - Optional response format specification. Supports:
   *   - `{ type: 'json_object' }` - Returns a JSON object
   *   - `{ type: 'json_schema', json_schema: { name: string, schema: object } }` - Returns JSON matching the schema
   *   - `MyZodSchema` - Pass a Zod schema directly for automatic JSON schema conversion
   * @param options.wait - For compatibility - ignored since predictions complete synchronously
   * @returns Promise resolving to the prediction result
   * 
   * @example
   * ```typescript
   * // Basic usage with JSON object response
   * const result = await client.run('gpt-4', {
   *   input: { prompt: 'Hello world' },
   *   response_format: { type: 'json_object' }
   * });
   * 
   * // Using Zod schema directly
   * import { z } from 'zod';
   * const UserSchema = z.object({
   *   name: z.string(),
   *   age: z.number()
   * });
   * 
   * const result = await client.run('gpt-4', {
   *   input: { prompt: 'Generate a user' },
   *   response_format: UserSchema
   * });
   * ```
   */
  async run(
    model: string,
    options: {
      input: Record<string, any>     // Can contain prompt OR messages array with multimodal content
      response_format?: ResponseFormat
      wait?: boolean                 // For compatibility - ignored since predictions complete synchronously
    }
  ): Promise<any> {
    const { input, response_format } = options

    this.debug('Starting prediction', { model, input, response_format })

    // Make direct API call and return output
    const requestPayload: any = {
      model,
      input: options.input
    }
    
    if (options.response_format) {
      requestPayload.response_format = this.processResponseFormat(options.response_format)
    }
    
    const response = await this.makeRequest('POST', '/v1/run', requestPayload)

    this.debug('Prediction completed', { 
      hasOutput: !!response.output 
    })

    // Emit usage-changed event after successful run
    this.emit('usage-changed', { operation: 'run', model })

    // Return just the output from the API
    return response
  }

  /**
   * Process response format, converting Zod schemas to json_schema format
   */
  private processResponseFormat(responseFormat: ResponseFormat): ResponseFormat {
    // Handle native Zod schema passed directly
    if (this.isZodSchema(responseFormat)) {
      return this.convertZodSchema(responseFormat, 'native Zod schema')
    }
    
    // Return other formats as-is (json_object, json_schema, etc.)
    return responseFormat
  }

  /**
   * Check if the provided object looks like a Zod schema
   */
  private isZodSchema(obj: any): boolean {
    return obj && 
           typeof obj === 'object' && 
           (typeof obj._def === 'object' || typeof obj.parse === 'function') &&
           // Make sure it's not already a proper ResponseFormat object
           !obj.type
  }

  /**
   * Convert a Zod schema to json_schema format
   */
  private convertZodSchema(zodSchema: any, source: string): ResponseFormat {
    if (!zodSchema || typeof zodSchema !== 'object') {
      this.debug('Invalid Zod schema provided, falling back to json_object', { source })
      return { type: 'json_object' }
    }

    try {
      // Convert the OpenAI format to our ResponseFormat type
      return {
        type: 'json_schema',
        json_schema: {
          name: zodSchema._def?.typeName || 'ZodSchema',
          strict: true,
          schema: z.toJSONSchema(zodSchema)
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.debug('Failed to convert Zod schema, falling back to json_object', { error: errorMessage, source })
      // Fallback to json_object if conversion fails
      return { type: 'json_object' }
    }
  }

  /**
   * Get account balance information
   */
  async getBalance(): Promise<BalanceInfo> {
    const response = await this.makeRequest('GET', '/v1/subscriptions/usage')
    const userBalance = response.credits?.userBalance
    return {
      allocatedCredits: userBalance?.allocatedCredits || 0,
      usedCredits: userBalance?.usedCredits || 0,
      remainingCredits: userBalance?.remainingCredits || 0
    }
  }

  /**
   * Get user storage data using auth context
   */
  async getStorage(options: StorageOptions = {}): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('GET', url)
  }

  /**
   * Update user storage data using auth context
   */
  async setStorage(
    sessionData: Record<string, any>, 
    options: StorageOptions = {}
  ): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('PUT', url, { sessionData })
  }

  /**
   * Delete user storage data using auth context
   */
  async deleteStorage(options: StorageOptions = {}): Promise<void> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    await this.makeRequest('DELETE', url)
  }

  /**
   * Get user's subscription status using userKey authentication
   * Requires userKey to be provided in client configuration
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    console.log('[SubscribeDevClient] getSubscriptionStatus called')

    if (!this.config.userKey) {
      console.log('[SubscribeDevClient] No userKey provided, throwing ValidationError')
      throw new ValidationError(
        'User key is required to get subscription status. Provide userKey in client configuration.',
        { method: 'getSubscriptionStatus' }
      )
    }

    console.log('[SubscribeDevClient] Making request to /v1/subscriptions/plan with userKey:', this.config.userKey.substring(0, 10) + '...')

    try {
      const response = await this.makeRequest('GET', '/v1/subscriptions/plan')
      console.log('[SubscribeDevClient] Received response:', {
        hasUserPlan: !!response.userPlan,
        planId: response.userPlan?.planId,
        status: response.userPlan?.status,
        hasPlanDetails: !!response.userPlan?.plan
      })
      
      const userPlan = response.userPlan
      
      // Handle case where user has no subscription (status: 'none')
      if (!userPlan || userPlan.status === 'none' || !userPlan.plan) {
        console.log('[SubscribeDevClient] User has no active subscription, returning none status')
        return {
          hasActiveSubscription: false,
          status: userPlan?.status || 'none'
        }
      }

      const result = {
        hasActiveSubscription: userPlan.status === 'active',
        plan: userPlan.plan ? {
          id: userPlan.plan.id || userPlan.planId,
          name: userPlan.plan.name,
          price: userPlan.plan.price,
          tokenLimit: userPlan.plan.tokenLimit || userPlan.plan.token_limit,
          subtitle: userPlan.plan.subtitle,
          description: userPlan.plan.description,
          features: userPlan.plan.features
        } : undefined,
        status: userPlan.status,
        startedAt: userPlan.startedAt,
        endsAt: userPlan.endsAt
      }

      console.log('[SubscribeDevClient] Returning subscription status:', {
        hasActiveSubscription: result.hasActiveSubscription,
        status: result.status,
        planName: result.plan?.name
      })

      return result
    } catch (error: any) {
      console.log('[SubscribeDevClient] Error in getSubscriptionStatus:', {
        errorType: error.constructor.name,
        message: error.message
      })
      
      // Re-throw any genuine errors
      throw error
    }
  }

  /**
   * Get comprehensive usage limits and current consumption
   * Requires userKey to be provided in client configuration
   */
  async getUsageLimits(): Promise<UsageLimits> {
    if (!this.config.userKey) {
      throw new ValidationError(
          'User key is required to get usage limits. Provide userKey in client configuration.',
          { method: 'getUsageLimits' }
      )
    }
    return this.makeRequest('GET', '/v1/subscriptions/usage')
  }

  /**
   * Get comprehensive usage information including credits, rate limits, and consumption
   * Alias for getUsageLimits() - Requires userKey to be provided in client configuration
   */
  async getUsage(): Promise<UsageLimits> {
    return this.getUsageLimits()
  }

  /**
   * Get or create a demo project with a public API key
   * Requires userKey (userToken) to be provided in client configuration
   * The returned API key is public and safe to share
   */
  async getDemoProject(): Promise<DemoProject> {
    if (!this.config.userKey) {
      throw new ValidationError(
        'User key is required to get demo project. Provide userKey in client configuration.',
        { method: 'getDemoProject' }
      )
    }

    this.debug('Getting demo project')
    
    const response = await this.makePlatformRequest('POST', '/v1/platform/projects/demo')
    
    this.debug('Demo project retrieved', { 
      hasApiKey: !!response.apiKey,
      projectId: response.id,
      isDemo: response.isDemo
    });

    // Store the API key locally and log it
    const demoApiKey = response.apiKey
    console.log('Demo project API key (public and safe to share):', demoApiKey)

    return response
  }

  /**
   * Make authenticated HTTP request to platform API using userToken as Bearer token
   */
  private async makePlatformRequest(
    method: string,
    path: string,
    body?: any
  ): Promise<any> {
    const url = `${this.config.baseUrl}${path}`
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.config.userKey}`,
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this.config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
    }

    this.debug('Making platform request', { method, url, headers: { ...headers, Authorization: '[redacted]' } })

    const response = await this.fetch(url, requestOptions)
    
    // Handle response
    const responseText = await response.text()
    let responseData: any
    
    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      responseData = { error: { message: responseText } }
    }

    if (!response.ok) {
      this.handleErrorResponse(response, responseData)
    }

    this.debug('Platform request successful', { status: response.status, data: responseData })
    return responseData
  }

  /**
   * Make authenticated HTTP request to SubscribeDev API
   */
  private async makeRequest(
    method: string,
    path: string,
    body?: any
  ): Promise<any> {
    const url = `${this.config.baseUrl}${path}`
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.config.apiKey}`,
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`
    }

    // Add user token if provided
    if (this.config.userKey) {
      headers['X-User-Token'] = this.config.userKey
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this.config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
    }

    this.debug('Making request', { method, url, headers: { ...headers, Authorization: '[redacted]' } })

    const response = await this.fetch(url, requestOptions)
    
    // Handle response
    const responseText = await response.text()
    let responseData: any
    
    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      responseData = { error: { message: responseText } }
    }

    if (!response.ok) {
      this.handleErrorResponse(response, responseData)
    }

    this.debug('Request successful', { status: response.status, data: responseData })
    return responseData
  }

  /**
   * Handle error responses and throw appropriate error types
   */
  private handleErrorResponse(response: Response, data: any): never {
    const requestId = response.headers.get('x-request-id') || undefined
    const error = data?.error || {}
    const message = error.message || `HTTP ${response.status}`

    switch (response.status) {
      case 400:
        throw new ValidationError(message, error.details, requestId)
      case 401:
        throw new AuthenticationError(message, requestId)
      case 402:
        // Check if this is an unsubscribed error vs insufficient balance
        if (error.code === 'USER_UNSUBSCRIBED' || message.toLowerCase().includes('unsubscribed') || message.toLowerCase().includes('subscription')) {
          throw new UnsubscribedError(message, requestId)
        }
        throw new InsufficientBalanceError(
          message,
          error.details?.remainingCredits,
          error.details?.requiredCredits,
          requestId
        )
      case 403:
        throw new AccessDeniedError(message, requestId)
      case 404:
        throw new NotFoundError(message, requestId)
      case 429:
        throw new RateLimitError(
          message,
          error.details?.resetTime,
          error.details?.retryAfter,
          requestId
        )
      default:
        throw new SubscribeDevError(
          message,
          error.code || 'UNKNOWN_ERROR',
          response.status,
          error.details,
          requestId
        )
    }
  }

  /**
   * Debug logging
   */
  private debug(message: string, data?: any) {
    if (this.config.debug) {
      console.log(`[SubscribeDevClient] ${message}`, data || '')
    }
  }


  /**
   * Static method to create client (for convenience)
   */
  static create(config: SubscribeDevClientConfig): SubscribeDevClient {
    return new SubscribeDevClient(config)
  }
}